import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Play, Calendar, Clock, ArrowLeft, Headphones } from "lucide-react";
import { Reveal } from "../components/common";
import { publicApi } from "../lib/api";
import VideoModal, { VideoThumb } from "../components/VideoModal";
import EpisodeDescription from "../components/EpisodeDescription";
import Seo from "../components/Seo";

const formatDate = (d) => (d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "");

export default function AllEpisodes() {
  const [podcasts, setPodcasts] = useState([]);
  const [active, setActive] = useState(null);
  const [seriesMap, setSeriesMap] = useState({});
  const [params] = useSearchParams();
  useEffect(() => {
    publicApi.series().then((list) => setSeriesMap(Object.fromEntries((list || []).map((s) => [s.slug, s.image])))).catch(() => {});
    publicApi.allPodcasts().then((eps) => {
      setPodcasts(eps);
      const watch = params.get("watch");
      if (watch) {
        const match = eps.find((e) => e.youtubeId === watch);
        if (match) setActive(match);
      }
    }).catch(() => {});
  }, [params]);

  return (
    <div>
      <Seo title="All Podcast Episodes" description="Browse every episode of More Than Numbers — the CASS podcast on business, tax and cloud accounting." />
      {active && <VideoModal videoId={active.youtubeId} title={active.title} onClose={() => setActive(null)} />}
      <section className="numbers-pattern text-white">
        <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-24">
          <Link to="/podcast" className="inline-flex items-center gap-2 text-slate-300 hover:text-orange text-sm font-semibold mb-6 transition-colors"><ArrowLeft size={16} /> Back to podcast</Link>
          <span className="text-orange font-bold text-sm tracking-widest uppercase">Full archive</span>
          <h1 className="font-display font-extrabold text-4xl md:text-6xl uppercase leading-[0.95] mt-3">All episodes</h1>
          <p className="mt-5 text-slate-300 max-w-2xl leading-relaxed">Every episode of More Than Numbers in one place — browse and binge.</p>
        </div>
      </section>

      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          {podcasts.length === 0 ? (
            <div className="text-center text-slate-400 py-16"><Headphones size={40} className="mx-auto mb-4 text-slate-300" />No episodes yet.</div>
          ) : (
            <div className="space-y-6">
              {podcasts.map((p, i) => (
                <Reveal key={p.id} delay={i * 60}>
                  <button type="button" data-testid={`archive-episode-${p.id}`} onClick={() => setActive(p)} className="w-full text-left group grid md:grid-cols-[280px_1fr] gap-6 bg-slate-50 rounded-2xl overflow-hidden border border-slate-100 hover:shadow-lg transition-all">
                    <div className="relative aspect-video overflow-hidden bg-navy self-center">
                      <VideoThumb src={p.image} youtubeId={p.youtubeId} fallback={seriesMap[p.series]} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      <div className="absolute inset-0 bg-navy/40 flex items-center justify-center">
                        <span className="w-14 h-14 rounded-full bg-orange flex items-center justify-center group-hover:scale-110 transition-transform shadow-xl"><Play size={22} className="text-white ml-1" fill="white" /></span>
                      </div>
                    </div>
                    <div className="p-6 md:py-8 md:pr-8">
                      {p.episode && <span className="inline-block bg-orange/10 text-orange text-xs font-bold px-3 py-1 rounded-full mb-3">{p.episode}</span>}
                      <h3 className="font-display font-bold text-xl md:text-2xl text-navy leading-snug mb-3 group-hover:text-orange transition-colors">{p.title}</h3>
                      <EpisodeDescription text={p.description} className="text-slate-500 leading-relaxed mb-4" />
                      <div className="flex items-center gap-5 text-xs text-slate-400">
                        <span className="flex items-center gap-1.5"><Calendar size={13} /> {formatDate(p.date)}</span>
                        <span className="flex items-center gap-1.5"><Clock size={13} /> {p.duration}</span>
                      </div>
                    </div>
                  </button>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
